import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Topbar from "../../components/common/Topbar";
import Toast from "../../components/common/Toast";
import { showToast } from "../../store/uiSlice";
import { setBalance } from "../../store/tradeSlice";
import { fmtUSD } from "../../utils/formatters";
import api from "../../services/api";

// UPI per-transaction cap (mirrors backend enforcement)
const UPI_MAX_WITHDRAWAL = 200;

const Withdrawal = () => {
  const dispatch = useDispatch();
  const { balance } = useSelector((state) => state.trade);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("payalma");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // PayAlma specific state
  const [payalmaDetails, setPayalmaDetails] = useState({
    accountName: "",
    accountNumber: "",
    bankName: "",
    bankCode: "",
    terminalName: "exxogate",
    paymentMethod: "exxogate",
  });
  const [payalmaMethods, setPayalmaMethods] = useState([]);
  const [payalmaLoading, setPayalmaLoading] = useState(false);

  // Bank Transfer specific state
  const [bankDetails, setBankDetails] = useState({
    accountName: "",
    accountNumber: "",
    bankName: "",
    bankCode: "",
    swiftCode: "",
    branchName: "",
    accountType: "savings",
  });

  // UPI specific state
  const [upiDetails, setUpiDetails] = useState({
    upiId: "",
    accountName: "",
  });

  // Other methods
  const [cryptoDetails, setCryptoDetails] = useState({
    walletAddress: "",
    network: "ERC20",
  });
  const [cardDetails, setCardDetails] = useState({
    cardNumber: "",
    cardholderName: "",
    expiryMonth: "",
    expiryYear: "",
  });
  const [ewalletDetails, setEwalletDetails] = useState({
    email: "",
    provider: "PayPal",
  });

  // Withdrawal history state
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState({
    totalWithdrawals: 0,
    withdrawalCount: 0,
    pendingCount: 0,
  });

  useEffect(() => {
    fetchSavedPaymentMethods();
    fetchWithdrawalHistory();
    fetchPayalmaMethods();
  }, []);

  const fetchSavedPaymentMethods = async () => {
    try {
      const response = await api.get("/user/payment-methods");
      if (response.data.bankAccount) {
        setPayalmaDetails((prev) => ({
          ...prev,
          ...response.data.bankAccount,
        }));
        setBankDetails((prev) => ({
          ...prev,
          ...response.data.bankAccount,
        }));
      }
    } catch (error) {}
  };

  const fetchPayalmaMethods = async () => {
    setPayalmaLoading(true);
    try {
      const response = await api.get("/withdrawal/payalma/methods");
      if (response.data.success) {
        setPayalmaMethods(response.data.methods || []);
      }
    } catch (error) {
      console.error("Failed to fetch PayAlma methods:", error);
    } finally {
      setPayalmaLoading(false);
    }
  };

  const fetchWithdrawalHistory = async () => {
    try {
      const response = await api.get("/withdrawal/history");
      const allWithdrawals = response.data || [];
      setWithdrawalHistory(allWithdrawals);
      const completed = allWithdrawals.filter((w) => w.status === "completed");
      const pending = allWithdrawals.filter((w) => w.status === "pending");
      const processing = allWithdrawals.filter((w) => w.status === "processing");
      const total = completed.reduce(
        (sum, w) => sum + Number(w.amount || 0),
        0,
      );
      setHistoryStats({
        totalWithdrawals: total,
        withdrawalCount: completed.length,
        pendingCount: pending.length + processing.length,
      });
    } catch (error) {
      console.error("Failed to fetch withdrawal history:", error);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "completed") {
      return (
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-bold"
          style={{
            background: "var(--color-greenBg)",
            color: "var(--color-green)",
            border: "1px solid var(--color-greenBorder)",
          }}
        >
          <i className="fas fa-check-circle text-[10px]"></i>
          COMPLETED
        </span>
      );
    }
    if (status === "pending" || status === "processing") {
      return (
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-bold"
          style={{
            background: "var(--color-yellowBg)",
            color: "var(--color-yellow)",
            border: "1px solid var(--color-yellowBorder)",
          }}
        >
          <i className="fas fa-clock text-[10px]"></i>
          {status === "processing" ? "PROCESSING" : "PENDING"}
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-bold"
        style={{
          background: "var(--color-redBg)",
          color: "var(--color-red)",
          border: "1px solid var(--color-redBorder)",
        }}
      >
        <i className="fas fa-times-circle text-[10px]"></i>
        {status === "failed" ? "FAILED" : "REJECTED"}
      </span>
    );
  };

  const validateWithdrawal = () => {
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum < 5) {
      dispatch(
        showToast({ message: "Minimum withdrawal is $5.00", type: "error" }),
      );
      return false;
    }
    if (amountNum > balance) {
      dispatch(
        showToast({
          message: `Insufficient balance. Max withdrawal: ${fmtUSD(balance)}`,
          type: "error",
        }),
      );
      return false;
    }

    // UPI-specific max limit of $200 per withdrawal
    if (method === "upi" && amountNum > UPI_MAX_WITHDRAWAL) {
      dispatch(
        showToast({
          message: `UPI withdrawals are limited to $${UPI_MAX_WITHDRAWAL.toFixed(2)} per transaction`,
          type: "error",
        }),
      );
      return false;
    }

    if (method === "payalma") {
      if (!payalmaDetails.accountName?.trim()) {
        dispatch(
          showToast({
            message: "Account holder name is required",
            type: "error",
          }),
        );
        return false;
      }
      if (!payalmaDetails.accountNumber?.trim()) {
        dispatch(
          showToast({ message: "Account number is required", type: "error" }),
        );
        return false;
      }
      if (!payalmaDetails.bankName?.trim()) {
        dispatch(
          showToast({ message: "Bank name is required", type: "error" }),
        );
        return false;
      }
      if (!payalmaDetails.bankCode?.trim()) {
        dispatch(
          showToast({ message: "Bank code / IFSC is required", type: "error" }),
        );
        return false;
      }
    } else if (method === "bank") {
      if (!bankDetails.accountName?.trim()) {
        dispatch(
          showToast({
            message: "Account holder name is required",
            type: "error",
          }),
        );
        return false;
      }
      if (!bankDetails.accountNumber?.trim()) {
        dispatch(
          showToast({ message: "Account number is required", type: "error" }),
        );
        return false;
      }
      if (!bankDetails.bankName?.trim()) {
        dispatch(
          showToast({ message: "Bank name is required", type: "error" }),
        );
        return false;
      }
      if (!bankDetails.bankCode?.trim() && !bankDetails.swiftCode?.trim()) {
        dispatch(
          showToast({
            message: "Bank code / IFSC or SWIFT code is required",
            type: "error",
          }),
        );
        return false;
      }
    } else if (method === "upi") {
      if (!upiDetails.upiId?.trim()) {
        dispatch(
          showToast({ message: "UPI ID is required", type: "error" }),
        );
        return false;
      }
      if (!/^[\w.\-]{2,256}@[a-zA-Z]{2,64}$/.test(upiDetails.upiId.trim())) {
        dispatch(
          showToast({
            message: "Enter a valid UPI ID (e.g. name@bank)",
            type: "error",
          }),
        );
        return false;
      }
    } else if (method === "crypto") {
      if (!cryptoDetails.walletAddress?.trim()) {
        dispatch(
          showToast({ message: "Wallet address is required", type: "error" }),
        );
        return false;
      }
    } else if (method === "card") {
      if (!cardDetails.cardNumber?.trim()) {
        dispatch(
          showToast({ message: "Card number is required", type: "error" }),
        );
        return false;
      }
      if (!cardDetails.cardholderName?.trim()) {
        dispatch(
          showToast({ message: "Cardholder name is required", type: "error" }),
        );
        return false;
      }
    } else if (method === "ewallet") {
      if (!ewalletDetails.email?.trim()) {
        dispatch(
          showToast({ message: "E-wallet email is required", type: "error" }),
        );
        return false;
      }
    }
    return true;
  };

  const setPresetAmount = (preset) => {
    if (method === "upi" && preset > UPI_MAX_WITHDRAWAL) {
      dispatch(
        showToast({
          message: `UPI withdrawals are limited to $${UPI_MAX_WITHDRAWAL.toFixed(2)} per transaction`,
          type: "error",
        }),
      );
      return;
    }
    if (preset <= balance) setAmount(preset);
    else
      dispatch(
        showToast({
          message: `Amount exceeds available balance (${fmtUSD(balance)})`,
          type: "error",
        }),
      );
  };

  const processWithdrawal = async () => {
    if (!validateWithdrawal()) return;
    setIsProcessing(true);
    try {
      let methodDetails;

      if (method === "payalma") {
        methodDetails = {
          ...payalmaDetails,
          terminalName: payalmaDetails.terminalName || "exxogate",
          paymentMethod: payalmaDetails.terminalName || "exxogate",
        };
      } else if (method === "bank") {
        methodDetails = bankDetails;
      } else if (method === "upi") {
        methodDetails = {
          upiId: upiDetails.upiId.trim(),
          accountName: upiDetails.accountName.trim(),
        };
      } else if (method === "crypto") {
        methodDetails = cryptoDetails;
      } else if (method === "card") {
        methodDetails = cardDetails;
      } else {
        methodDetails = ewalletDetails;
      }

      const response = await api.post("/withdrawal", {
        amount: parseFloat(amount),
        method,
        methodDetails,
      });

      if (response.data.success) {
        const balanceResponse = await api.get("/user/balance");
        dispatch(setBalance(balanceResponse.data.balance));
        dispatch(
          showToast({
            message: `✅ Withdrawal request of ${fmtUSD(parseFloat(amount))} submitted for review!`,
            type: "success",
          }),
        );
        setAmount("");
        setPayalmaDetails({
          accountName: "",
          accountNumber: "",
          bankName: "",
          bankCode: "",
          terminalName: "exxogate",
          paymentMethod: "exxogate",
        });
        setBankDetails({
          accountName: "",
          accountNumber: "",
          bankName: "",
          bankCode: "",
          swiftCode: "",
          branchName: "",
          accountType: "savings",
        });
        setUpiDetails({ upiId: "", accountName: "" });
        fetchWithdrawalHistory();
      }
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.data?.message || "Withdrawal failed",
          type: "error",
        }),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const verifyWithdrawalStatus = async (withdrawalId) => {
    setIsVerifying(true);
    try {
      const response = await api.get(`/withdrawal/${withdrawalId}/verify`);

      if (response.data.verified) {
        dispatch(
          showToast({
            message: "✅ Withdrawal completed successfully!",
            type: "success",
          }),
        );
        fetchWithdrawalHistory();
      } else if (response.data.status === "failed") {
        dispatch(
          showToast({
            message: "❌ Withdrawal failed. Balance has been refunded.",
            type: "error",
          }),
        );
        fetchWithdrawalHistory();
      } else {
        dispatch(
          showToast({
            message: "⏳ Withdrawal is still processing...",
            type: "info",
          }),
        );
      }
    } catch (error) {
      console.error("Verification failed:", error);
    } finally {
      setIsVerifying(false);
    }
  };

  const inputStyle = {
    background: "var(--color-bgInput)",
    border: "1px solid var(--color-borderMedium)",
    color: "var(--color-text)",
    outline: "none",
  };
  const cardStyle = {
    background: "var(--color-bgSurface)",
    border: "1px solid var(--color-border)",
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--color-bg)" }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div
          className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float"
          style={{
            background:
              "radial-gradient(circle, rgba(46,54,66,0.08), transparent 65%)",
          }}
        />
        <div
          className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000"
          style={{
            background:
              "radial-gradient(circle, rgba(139,92,246,0.05), transparent 65%)",
          }}
        />
      </div>
      <Topbar />

      <div className="relative z-10 pt-[52px] p-5">
        <div className="w-full max-w-[700px] mx-auto">
          <div className="text-center mb-8 animate-slide-up">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
              style={{
                background: "var(--color-orangeBg)",
                border: "1px solid var(--color-orangeBorder)",
              }}
            >
              <i
                className="fas fa-circle-minus text-[9px]"
                style={{ color: "var(--color-text)" }}
              />
              <span
                className="text-[9px] font-mono tracking-wide"
                style={{ color: "var(--color-text)" }}
              >
                FUNDS
              </span>
            </div>
            <h1
              className="font-syne text-3xl md:text-4xl font-extrabold mb-2"
              style={{ color: "var(--color-text)" }}
            >
              Request{" "}
              <span style={{ color: "var(--color-text)" }}>Withdrawal</span>
            </h1>
            <p
              className="text-sm"
              style={{ color: "var(--color-textSecondary)" }}
            >
              Withdraw funds from your trading account
            </p>
          </div>

          {/* Single Column Layout - Top to Bottom */}
          <div className="space-y-6">
            {/* Withdrawal Form - TOP */}
            <div className="animate-slide-up animation-delay-100">
              <div className="rounded-2xl overflow-hidden" style={cardStyle}>
                <div
                  className="flex items-center gap-3 px-6 py-4 border-b"
                  style={{
                    background: "var(--color-bgCard)",
                    borderColor: "var(--color-borderMedium)",
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{
                      background: "var(--color-orangeBg)",
                      border: "1px solid var(--color-orangeBorder)",
                      color: "var(--color-text)",
                    }}
                  >
                    <i className="fas fa-money-bill-wave text-base" />
                  </div>
                  <div>
                    <div
                      className="font-syne text-base font-extrabold"
                      style={{ color: "var(--color-text)" }}
                    >
                      Withdrawal Request
                    </div>
                    <div
                      className="text-[9px] font-mono"
                      style={{ color: "var(--color-textDim)" }}
                    >
                      Enter amount and withdrawal details
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-5">
                  <div
                    className="text-center p-4 rounded-xl border"
                    style={{
                      background:
                        "linear-gradient(to right, var(--color-orangeBg), var(--color-purpleBg))",
                      borderColor: "var(--color-orangeBorder)",
                    }}
                  >
                    <div
                      className="text-[8px] font-mono tracking-wide uppercase mb-1"
                      style={{ color: "var(--color-textDim)" }}
                    >
                      Available Balance
                    </div>
                    <div
                      className="font-syne text-3xl font-extrabold"
                      style={{ color: "var(--color-text)" }}
                    >
                      {fmtUSD(balance)}
                    </div>
                  </div>

                  <div>
                    <div className="relative">
                      <span
                        className="absolute left-3 top-1/2 -translate-y-1/2 font-syne text-base font-extrabold"
                        style={{ color: "var(--color-text)" }}
                      >
                        $
                      </span>
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        min="5"
                        step="1"
                        className="w-full rounded-lg px-3 py-3 pl-7 font-dm text-sm"
                        style={inputStyle}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "var(--color-orange)")
                        }
                        onBlur={(e) =>
                          (e.target.style.borderColor =
                            "var(--color-borderMedium)")
                        }
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      {[100, 500, 1000, 5000].map((preset) => (
                        <button
                          key={preset}
                          onClick={() => setPresetAmount(preset)}
                          className="flex-1 py-1.5 rounded-lg font-mono text-[10px] font-semibold transition-all border"
                          style={{
                            background: "var(--color-bgCard)",
                            borderColor: "var(--color-borderMedium)",
                            color: "var(--color-textMuted)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--color-orange)";
                            e.currentTarget.style.color = "var(--color-orange)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--color-borderMedium)";
                            e.currentTarget.style.color =
                              "var(--color-textMuted)";
                          }}
                        >
                          ${preset.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label
                      className="text-[9px] font-mono tracking-wide uppercase block mb-2"
                      style={{ color: "var(--color-text)" }}
                    >
                      Withdrawal Method
                    </label>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {[
                        {
                          id: "payalma",
                          label: "PayAlma",
                          icon: "fa-building-columns",
                          badge: "RECOMMENDED",
                        },
                        {
                          id: "bank",
                          label: "Bank Transfer",
                          icon: "fa-bank",
                        },
                        {
                          id: "upi",
                          label: "UPI",
                          icon: "fa-mobile-screen-button",
                        },
                        {
                          id: "card",
                          label: "Credit/Debit Card",
                          icon: "fa-credit-card",
                        },
                        {
                          id: "crypto",
                          label: "Cryptocurrency",
                          icon: "fa-coins",
                        },
                        { id: "ewallet", label: "E-Wallet", icon: "fa-wallet" },
                      ].map((m) => (
                        <button
                          key={m.id}
                          onClick={() => setMethod(m.id)}
                          className="relative p-3 rounded-lg text-center transition-all border"
                          style={{
                            background:
                              method === m.id
                                ? "var(--color-orangeBg)"
                                : "var(--color-bgCard)",
                            borderColor:
                              method === m.id
                                ? "var(--color-orangeBorder)"
                                : "var(--color-borderMedium)",
                          }}
                        >
                          {m.badge && (
                            <span
                              className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[8px] font-bold"
                              style={{
                                background: "var(--color-green)",
                                color: "white",
                              }}
                            >
                              {m.badge}
                            </span>
                          )}
                          <i
                            className={`fas ${m.icon} text-xs mb-1 block`}
                            style={{
                              color:
                                method === m.id
                                  ? "var(--color-text)"
                                  : "var(--color-textSecondary)",
                            }}
                          />
                          <div
                            className="font-syne text-[9px] font-bold"
                            style={{ color: "var(--color-text)" }}
                          >
                            {m.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* PayAlma Withdrawal Form */}
                  {method === "payalma" && (
                    <div className="space-y-4">
                      <div
                        className="p-3 rounded-lg"
                        style={{
                          background: "var(--color-bgCard)",
                          border: "1px solid var(--color-orangeBorder)",
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <i
                            className="fas fa-shield-halved text-xs"
                            style={{ color: "var(--color-orange)" }}
                          />
                          <span
                            className="text-[10px] font-bold"
                            style={{ color: "var(--color-text)" }}
                          >
                            PayAlma Secure Payout
                          </span>
                        </div>
                        <p
                          className="text-[9px] mt-1"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Fast and secure transfers via PayAlma network
                        </p>
                      </div>

                      {payalmaLoading ? (
                        <div className="text-center py-4">
                          <span className="inline-block w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></span>
                          <p
                            className="text-[10px] mt-2"
                            style={{ color: "var(--color-textSecondary)" }}
                          >
                            Loading available payout methods...
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {[
                            {
                              label: "Account Holder Name",
                              value: payalmaDetails.accountName,
                              setter: (v) =>
                                setPayalmaDetails({
                                  ...payalmaDetails,
                                  accountName: v,
                                }),
                              placeholder: "Enter account holder name",
                              icon: "fa-user",
                            },
                            {
                              label: "Account Number",
                              value: payalmaDetails.accountNumber,
                              setter: (v) =>
                                setPayalmaDetails({
                                  ...payalmaDetails,
                                  accountNumber: v,
                                }),
                              placeholder: "Enter account number",
                              icon: "fa-hashtag",
                            },
                            {
                              label: "Bank Name",
                              value: payalmaDetails.bankName,
                              setter: (v) =>
                                setPayalmaDetails({
                                  ...payalmaDetails,
                                  bankName: v,
                                }),
                              placeholder: "Enter bank name",
                              icon: "fa-bank",
                            },
                            {
                              label: "Bank Code / IFSC",
                              value: payalmaDetails.bankCode,
                              setter: (v) =>
                                setPayalmaDetails({
                                  ...payalmaDetails,
                                  bankCode: v,
                                }),
                              placeholder: "Enter bank code or IFSC",
                              icon: "fa-code",
                            },
                          ].map((field) => (
                            <div key={field.label}>
                              <label
                                className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                                style={{ color: "var(--color-text)" }}
                              >
                                {field.label}{" "}
                                <span style={{ color: "var(--color-red)" }}>
                                  *
                                </span>
                              </label>
                              <div className="relative">
                                <i
                                  className={`fas ${field.icon} absolute left-3 top-1/2 -translate-y-1/2 text-xs`}
                                  style={{ color: "var(--color-textDim)" }}
                                />
                                <input
                                  type="text"
                                  value={field.value}
                                  onChange={(e) => field.setter(e.target.value)}
                                  placeholder={field.placeholder}
                                  className="w-full rounded-lg px-3 py-2 pl-9 font-dm text-sm"
                                  style={inputStyle}
                                  onFocus={(e) =>
                                    (e.target.style.borderColor =
                                      "var(--color-orange)")
                                  }
                                  onBlur={(e) =>
                                    (e.target.style.borderColor =
                                      "var(--color-borderMedium)")
                                  }
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bank Transfer Form */}
                  {method === "bank" && (
                    <div className="space-y-4">
                      <div
                        className="p-3 rounded-lg"
                        style={{
                          background: "var(--color-bgCard)",
                          border: "1px solid var(--color-borderMedium)",
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <i
                            className="fas fa-bank text-xs"
                            style={{ color: "var(--color-textSecondary)" }}
                          />
                          <span
                            className="text-[10px] font-bold"
                            style={{ color: "var(--color-text)" }}
                          >
                            Bank Transfer
                          </span>
                        </div>
                        <p
                          className="text-[9px] mt-1"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Direct bank transfer to your account (1-3 business days)
                        </p>
                      </div>

                      <div className="space-y-3">
                        {[
                          {
                            label: "Account Holder Name",
                            value: bankDetails.accountName,
                            setter: (v) =>
                              setBankDetails({
                                ...bankDetails,
                                accountName: v,
                              }),
                            placeholder: "Enter account holder name",
                            icon: "fa-user",
                          },
                          {
                            label: "Account Number",
                            value: bankDetails.accountNumber,
                            setter: (v) =>
                              setBankDetails({
                                ...bankDetails,
                                accountNumber: v,
                              }),
                            placeholder: "Enter account number",
                            icon: "fa-hashtag",
                          },
                          {
                            label: "Bank Name",
                            value: bankDetails.bankName,
                            setter: (v) =>
                              setBankDetails({
                                ...bankDetails,
                                bankName: v,
                              }),
                            placeholder: "Enter bank name",
                            icon: "fa-bank",
                          },
                          {
                            label: "Bank Code / IFSC",
                            value: bankDetails.bankCode,
                            setter: (v) =>
                              setBankDetails({
                                ...bankDetails,
                                bankCode: v,
                              }),
                            placeholder: "Enter bank code or IFSC",
                            icon: "fa-code",
                          },
                          {
                            label: "SWIFT Code (Optional)",
                            value: bankDetails.swiftCode,
                            setter: (v) =>
                              setBankDetails({
                                ...bankDetails,
                                swiftCode: v,
                              }),
                            placeholder: "Enter SWIFT code",
                            icon: "fa-globe",
                          },
                          {
                            label: "Branch Name (Optional)",
                            value: bankDetails.branchName,
                            setter: (v) =>
                              setBankDetails({
                                ...bankDetails,
                                branchName: v,
                              }),
                            placeholder: "Enter branch name",
                            icon: "fa-building",
                          },
                        ].map((field) => (
                          <div key={field.label}>
                            <label
                              className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                              style={{ color: "var(--color-text)" }}
                            >
                              {field.label}{" "}
                              {!field.label.includes("Optional") && (
                                <span style={{ color: "var(--color-red)" }}>
                                  *
                                </span>
                              )}
                            </label>
                            <div className="relative">
                              <i
                                className={`fas ${field.icon} absolute left-3 top-1/2 -translate-y-1/2 text-xs`}
                                style={{ color: "var(--color-textDim)" }}
                              />
                              <input
                                type="text"
                                value={field.value}
                                onChange={(e) => field.setter(e.target.value)}
                                placeholder={field.placeholder}
                                className="w-full rounded-lg px-3 py-2 pl-9 font-dm text-sm"
                                style={inputStyle}
                                onFocus={(e) =>
                                  (e.target.style.borderColor =
                                    "var(--color-orange)")
                                }
                                onBlur={(e) =>
                                  (e.target.style.borderColor =
                                    "var(--color-borderMedium)")
                                }
                              />
                            </div>
                          </div>
                        ))}

                        {/* Account Type Selector */}
                        <div>
                          <label
                            className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                            style={{ color: "var(--color-text)" }}
                          >
                            Account Type
                          </label>
                          <select
                            value={bankDetails.accountType}
                            onChange={(e) =>
                              setBankDetails({
                                ...bankDetails,
                                accountType: e.target.value,
                              })
                            }
                            className="w-full rounded-lg px-3 py-2 font-dm text-sm"
                            style={inputStyle}
                          >
                            <option value="savings">Savings Account</option>
                            <option value="checking">Checking Account</option>
                            <option value="current">Current Account</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* UPI Withdrawal Form */}
                  {method === "upi" && (
                    <div className="space-y-4">
                      <div
                        className="p-3 rounded-lg"
                        style={{
                          background: "var(--color-bgCard)",
                          border: "1px solid var(--color-borderMedium)",
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <i
                            className="fas fa-mobile-screen-button text-xs"
                            style={{ color: "var(--color-textSecondary)" }}
                          />
                          <span
                            className="text-[10px] font-bold"
                            style={{ color: "var(--color-text)" }}
                          >
                            UPI Transfer
                          </span>
                        </div>
                        <p
                          className="text-[9px] mt-1"
                          style={{ color: "var(--color-textSecondary)" }}
                        >
                          Instant payout to your UPI ID
                        </p>
                        <p
                          className="text-[9px] mt-1 font-semibold"
                          style={{ color: "var(--color-yellow)" }}
                        >
                          <i className="fas fa-circle-info mr-1" />
                          Max ${UPI_MAX_WITHDRAWAL.toFixed(2)} per UPI withdrawal
                        </p>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label
                            className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                            style={{ color: "var(--color-text)" }}
                          >
                            UPI ID{" "}
                            <span style={{ color: "var(--color-red)" }}>*</span>
                          </label>
                          <div className="relative">
                            <i
                              className="fas fa-at absolute left-3 top-1/2 -translate-y-1/2 text-xs"
                              style={{ color: "var(--color-textDim)" }}
                            />
                            <input
                              type="text"
                              value={upiDetails.upiId}
                              onChange={(e) =>
                                setUpiDetails({
                                  ...upiDetails,
                                  upiId: e.target.value,
                                })
                              }
                              placeholder="yourname@bank"
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                              className="w-full rounded-lg px-3 py-2 pl-9 font-mono text-sm"
                              style={inputStyle}
                              onFocus={(e) =>
                                (e.target.style.borderColor =
                                  "var(--color-orange)")
                              }
                              onBlur={(e) =>
                                (e.target.style.borderColor =
                                  "var(--color-borderMedium)")
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <label
                            className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                            style={{ color: "var(--color-text)" }}
                          >
                            Account Holder Name (Optional)
                          </label>
                          <div className="relative">
                            <i
                              className="fas fa-user absolute left-3 top-1/2 -translate-y-1/2 text-xs"
                              style={{ color: "var(--color-textDim)" }}
                            />
                            <input
                              type="text"
                              value={upiDetails.accountName}
                              onChange={(e) =>
                                setUpiDetails({
                                  ...upiDetails,
                                  accountName: e.target.value,
                                })
                              }
                              placeholder="Name registered with UPI"
                              className="w-full rounded-lg px-3 py-2 pl-9 font-dm text-sm"
                              style={inputStyle}
                              onFocus={(e) =>
                                (e.target.style.borderColor =
                                  "var(--color-orange)")
                              }
                              onBlur={(e) =>
                                (e.target.style.borderColor =
                                  "var(--color-borderMedium)")
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Card Withdrawal Form */}
                  {method === "card" && (
                    <div className="space-y-3">
                      <div>
                        <label
                          className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                          style={{ color: "var(--color-text)" }}
                        >
                          Cardholder Name <span style={{ color: "var(--color-red)" }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={cardDetails.cardholderName}
                          onChange={(e) =>
                            setCardDetails({
                              ...cardDetails,
                              cardholderName: e.target.value,
                            })
                          }
                          placeholder="Enter cardholder name"
                          className="w-full rounded-lg px-3 py-2 font-dm text-sm"
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label
                          className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                          style={{ color: "var(--color-text)" }}
                        >
                          Card Number <span style={{ color: "var(--color-red)" }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={cardDetails.cardNumber}
                          onChange={(e) =>
                            setCardDetails({
                              ...cardDetails,
                              cardNumber: e.target.value,
                            })
                          }
                          placeholder="Enter card number"
                          className="w-full rounded-lg px-3 py-2 font-mono text-sm"
                          style={inputStyle}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label
                            className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                            style={{ color: "var(--color-text)" }}
                          >
                            Expiry Month
                          </label>
                          <input
                            type="text"
                            value={cardDetails.expiryMonth}
                            onChange={(e) =>
                              setCardDetails({
                                ...cardDetails,
                                expiryMonth: e.target.value,
                              })
                            }
                            placeholder="MM"
                            className="w-full rounded-lg px-3 py-2 font-dm text-sm"
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label
                            className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                            style={{ color: "var(--color-text)" }}
                          >
                            Expiry Year
                          </label>
                          <input
                            type="text"
                            value={cardDetails.expiryYear}
                            onChange={(e) =>
                              setCardDetails({
                                ...cardDetails,
                                expiryYear: e.target.value,
                              })
                            }
                            placeholder="YYYY"
                            className="w-full rounded-lg px-3 py-2 font-dm text-sm"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {method === "crypto" && (
                    <div className="space-y-3">
                      <div>
                        <label
                          className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                          style={{ color: "var(--color-text)" }}
                        >
                          Network
                        </label>
                        <select
                          value={cryptoDetails.network}
                          onChange={(e) =>
                            setCryptoDetails({
                              ...cryptoDetails,
                              network: e.target.value,
                            })
                          }
                          className="w-full rounded-lg px-3 py-2 font-dm text-sm"
                          style={inputStyle}
                        >
                          <option>ERC20 (Ethereum)</option>
                          <option>BEP20 (BSC)</option>
                          <option>TRC20 (TRON)</option>
                        </select>
                      </div>
                      <div>
                        <label
                          className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                          style={{ color: "var(--color-text)" }}
                        >
                          Wallet Address
                        </label>
                        <input
                          type="text"
                          value={cryptoDetails.walletAddress}
                          onChange={(e) =>
                            setCryptoDetails({
                              ...cryptoDetails,
                              walletAddress: e.target.value,
                            })
                          }
                          placeholder="Enter your wallet address"
                          className="w-full rounded-lg px-3 py-2 font-mono text-sm"
                          style={inputStyle}
                          onFocus={(e) =>
                            (e.target.style.borderColor = "var(--color-orange)")
                          }
                          onBlur={(e) =>
                            (e.target.style.borderColor =
                              "var(--color-borderMedium)")
                          }
                        />
                      </div>
                    </div>
                  )}

                  {method === "ewallet" && (
                    <div className="space-y-3">
                      <div>
                        <label
                          className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                          style={{ color: "var(--color-text)" }}
                        >
                          Provider
                        </label>
                        <select
                          value={ewalletDetails.provider}
                          onChange={(e) =>
                            setEwalletDetails({
                              ...ewalletDetails,
                              provider: e.target.value,
                            })
                          }
                          className="w-full rounded-lg px-3 py-2 font-dm text-sm"
                          style={inputStyle}
                        >
                          <option>PayPal</option>
                          <option>Skrill</option>
                          <option>Neteller</option>
                        </select>
                      </div>
                      <div>
                        <label
                          className="text-[8px] font-mono tracking-wide uppercase block mb-1"
                          style={{ color: "var(--color-text)" }}
                        >
                          Wallet Email / ID{" "}
                          <span style={{ color: "var(--color-red)" }}>*</span>
                        </label>
                        <input
                          type="email"
                          value={ewalletDetails.email}
                          onChange={(e) =>
                            setEwalletDetails({
                              ...ewalletDetails,
                              email: e.target.value,
                            })
                          }
                          placeholder="your@email.com"
                          className="w-full rounded-lg px-3 py-2 font-dm text-sm"
                          style={inputStyle}
                          onFocus={(e) =>
                            (e.target.style.borderColor = "var(--color-orange)")
                          }
                          onBlur={(e) =>
                            (e.target.style.borderColor =
                              "var(--color-borderMedium)")
                          }
                        />
                      </div>
                    </div>
                  )}

                  <button
                    onClick={processWithdrawal}
                    disabled={isProcessing}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-syne text-sm font-bold transition-all active:translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: "var(--color-gradientRed)",
                      color: "#ffffff",
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />{" "}
                        Processing
                      </>
                    ) : (
                      <>
                        <i className="fas fa-circle-minus mr-1" /> Request
                        Withdrawal
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Withdrawal History - BOTTOM */}
            <div className="animate-slide-up animation-delay-200">
              <div className="rounded-2xl overflow-hidden" style={cardStyle}>
                <div
                  className="flex items-center justify-between px-6 py-4 border-b"
                  style={{
                    background: "var(--color-bgCard)",
                    borderColor: "var(--color-borderMedium)",
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{
                        background: "var(--color-purpleBg)",
                        border: "1px solid var(--color-purpleBorder)",
                        color: "var(--color-purpleLight)",
                      }}
                    >
                      <i className="fas fa-clock-rotate-left text-base" />
                    </div>
                    <div>
                      <div
                        className="font-syne text-base font-extrabold"
                        style={{ color: "var(--color-text)" }}
                      >
                        Withdrawal History
                      </div>
                      <div
                        className="text-[10px] font-medium"
                        style={{ color: "var(--color-textSecondary)" }}
                      >
                        {historyStats.withdrawalCount} completed ·{" "}
                        {historyStats.pendingCount} pending
                      </div>
                    </div>
                  </div>
                  {historyStats.totalWithdrawals > 0 && (
                    <div className="text-right">
                      <div
                        className="text-[9px] font-mono tracking-wide uppercase mb-1"
                        style={{ color: "var(--color-textDim)" }}
                      >
                        Total Withdrawn
                      </div>
                      <div
                        className="font-syne text-xl font-extrabold"
                        style={{ color: "var(--color-red)" }}
                      >
                        {fmtUSD(historyStats.totalWithdrawals)}
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className="divide-y"
                  style={{ borderColor: "var(--color-borderLight)" }}
                >
                  {withdrawalHistory.length === 0 ? (
                    <div className="text-center py-16">
                      <i
                        className="fas fa-inbox text-3xl mb-3 opacity-30"
                        style={{ color: "var(--color-textDim)" }}
                      />
                      <p
                        className="text-xs font-medium"
                        style={{ color: "var(--color-textSecondary)" }}
                      >
                        No withdrawal history yet
                      </p>
                      <p
                        className="text-[10px] mt-1"
                        style={{ color: "var(--color-textDim)" }}
                      >
                        Your completed and pending withdrawals will appear here
                      </p>
                    </div>
                  ) : (
                    withdrawalHistory.map((withdrawal, idx) => (
                      <div
                        key={idx}
                        className="p-4 hover:bg-opacity-5 hover:bg-white transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{
                                background:
                                  withdrawal.status === "completed"
                                    ? "var(--color-greenBg)"
                                    : withdrawal.status === "pending" ||
                                        withdrawal.status === "processing"
                                      ? "var(--color-yellowBg)"
                                      : "var(--color-redBg)",
                                border: `2px solid ${
                                  withdrawal.status === "completed"
                                    ? "var(--color-greenBorder)"
                                    : withdrawal.status === "pending" ||
                                        withdrawal.status === "processing"
                                      ? "var(--color-yellowBorder)"
                                      : "var(--color-redBorder)"
                                }`,
                                color:
                                  withdrawal.status === "completed"
                                    ? "var(--color-green)"
                                    : withdrawal.status === "pending" ||
                                        withdrawal.status === "processing"
                                      ? "var(--color-yellow)"
                                      : "var(--color-red)",
                              }}
                            >
                              <i
                                className={`fas ${
                                  withdrawal.status === "completed"
                                    ? "fa-check"
                                    : withdrawal.status === "pending" ||
                                        withdrawal.status === "processing"
                                      ? "fa-clock"
                                      : "fa-times"
                                } text-sm`}
                              />
                            </div>
                            <div>
                              <div
                                className="font-syne text-sm font-extrabold"
                                style={{ color: "var(--color-text)" }}
                              >
                                {withdrawal.method === "payalma"
                                  ? "PayAlma Transfer"
                                  : withdrawal.method === "bank"
                                    ? "Bank Transfer"
                                    : withdrawal.method === "upi"
                                      ? "UPI Transfer"
                                      : withdrawal.method === "crypto"
                                        ? "Cryptocurrency"
                                        : withdrawal.method === "card"
                                          ? "Card Withdrawal"
                                          : withdrawal.method === "ewallet"
                                            ? "E-Wallet"
                                            : withdrawal.method || "Withdrawal"}
                              </div>
                              <div
                                className="text-[10px] font-medium mt-0.5"
                                style={{ color: "var(--color-textSecondary)" }}
                              >
                                {new Date(withdrawal.time).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div
                              className="font-syne text-lg font-extrabold"
                              style={{ color: "var(--color-red)" }}
                            >
                              -{fmtUSD(withdrawal.amount)}
                            </div>
                            <div className="mt-1">
                              {getStatusBadge(withdrawal.status)}
                            </div>
                          </div>
                        </div>

                        {/* Verify button for pending/processing PayAlma withdrawals */}
                        {(withdrawal.status === "pending" ||
                          withdrawal.status === "processing") &&
                          withdrawal.method === "payalma" && (
                            <div className="mt-2 flex justify-end">
                              <button
                                onClick={() =>
                                  verifyWithdrawalStatus(withdrawal.id)
                                }
                                disabled={isVerifying}
                                className="text-[10px] font-bold px-3 py-1.5 rounded-lg transition-all"
                                style={{
                                  background: "var(--color-orangeBg)",
                                  border: "1px solid var(--color-orangeBorder)",
                                  color: "var(--color-text)",
                                }}
                              >
                                {isVerifying ? (
                                  <>
                                    <span className="inline-block w-2.5 h-2.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                                    Checking...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-rotate mr-1" />
                                    Check Status
                                  </>
                                )}
                              </button>
                            </div>
                          )}

                        {/* Rejection Reason - Full Width Below */}
                        {(withdrawal.status === "rejected" ||
                          withdrawal.status === "failed") &&
                          withdrawal.rejectionReason && (
                            <div
                              className="mt-3 p-3 rounded-lg"
                              style={{
                                background: "var(--color-redBg)",
                                border: "1px solid var(--color-redBorder)",
                              }}
                            >
                              <div className="flex items-start gap-2">
                                <i
                                  className="fas fa-exclamation-circle text-xs mt-0.5 flex-shrink-0"
                                  style={{ color: "var(--color-red)" }}
                                ></i>
                                <div>
                                  <div
                                    className="text-[10px] font-extrabold uppercase mb-0.5"
                                    style={{ color: "var(--color-red)" }}
                                  >
                                    {withdrawal.status === "failed"
                                      ? "Failure Reason"
                                      : "Rejection Reason"}
                                  </div>
                                  <div
                                    className="text-[11px] font-medium leading-relaxed"
                                    style={{ color: "var(--color-text)" }}
                                  >
                                    {withdrawal.rejectionReason}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toast />
      <style>{`
        @keyframes float { 0%,100% { transform:translate(0,0) } 50% { transform:translate(28px,-28px) } }
        @keyframes slide-up { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        .animate-float { animation:float 20s ease-in-out infinite }
        .animation-delay-2000 { animation-delay:-8s }
        .animate-slide-up { animation:slide-up .6s ease-out both }
        .animation-delay-100 { animation-delay:.1s }
        .animation-delay-200 { animation-delay:.2s }
      `}</style>
    </div>
  );
};

export default Withdrawal;